import { createId as cryptoId } from 'crypto-id';
import { derived, signal, whenReadable, writable } from 'easy-signal';
import { Tab } from 'tab-election';
import { is, parse } from 'valibot';
import { JSONPatch } from '../json-patch';
import { LWW_DOC } from '../lib/config';
import { ChangeMessageSchema, ConfigSchema } from '../lib/schema';
import { debounce } from '../util';
import { agreeableWorker } from '../worker/worker';
import { getClientDoc } from './clientDoc';
import { proxyDoc } from './proxyDoc';
const activityEvents = ['keydown', 'mousemove', 'mousedown', 'touchstart', 'touchend'];
const EMPTY = {};
/**
 * Create an Agreeable client for the given schema.
 */
export function agreeableClient(config) {
    // Check the schema is valid
    parse(ConfigSchema, config);
    const clientDocs = new Map();
    const docSyncs = createDocSyncs();
    const uid = writable('');
    const state = writable({});
    const isOpen = writable(false);
    const onMessage = signal();
    let tabCreated;
    let tabExists = new Promise(resolve => (tabCreated = resolve));
    let defaultTextType;
    let tab;
    let worker = null;
    const client = {
        uid,
        state,
        isOpen,
        onMessage,
        open,
        close,
        getPatch,
        auth,
        sync,
        unsync,
        connect,
        disconnect,
        hasUncommittedChanges,
        send,
        now,
        call,
        createId,
    };
    for (const [name, docConfig] of Object.entries(config.children)) {
        client[name] = createCollection(name, docConfig);
    }
    const onActivity = debounce(() => call('hadActivity'), 10_000);
    return client;
    /** Functions to create the client objects */
    function open(options) {
        if (tab)
            close();
        uid.set(options.uid);
        defaultTextType = options.defaultTextType;
        // Create a Tab for the domain to forward requests on to the Worker
        tab = new Tab(`${config.name}/${options.appVersion}/${options.uid}`);
        tab.addEventListener('message', handleMessage);
        tab.addEventListener('state', handleState);
        if (globalThis.document) {
            activityEvents.forEach(event => document.addEventListener(event, onActivity));
        }
        if (globalThis.window) {
            window.addEventListener('focus', onActivity);
        }
        if (options.workerUrl === 'local') {
            agreeableWorker(config, options);
        }
        else if (options.workerUrl) {
            // Launch the worker
            const url = new URL(options.workerUrl, location.href);
            const useShared = url.searchParams.has('shared');
            url.searchParams.set('uid', options.uid);
            if (options.appVersion)
                url.searchParams.set('appVersion', options.appVersion);
            const WorkerClass = useShared && globalThis.SharedWorker ? SharedWorker : Worker;
            worker = new WorkerClass(url, { name: uid.get() });
        }
        tabCreated();
        isOpen.set(true);
    }
    function close() {
        if (tab) {
            tab.removeEventListener('message', handleMessage);
            tab.removeEventListener('state', handleState);
            tab.close();
        }
        if (globalThis.document) {
            activityEvents.forEach(event => document.removeEventListener(event, onActivity));
        }
        if (globalThis.window) {
            window.removeEventListener('focus', onActivity);
        }
        if (worker instanceof Worker)
            worker.terminate();
        else if (worker)
            worker.port?.close();
        worker = null;
        clientDocs.forEach(ref => ref.deref()?.unload());
        tabExists = new Promise(resolve => (tabCreated = resolve));
        isOpen.set(false);
    }
    function auth(idToken, delegate) {
        return call('auth', idToken, delegate);
    }
    function connect() {
        return call('connect');
    }
    function disconnect() {
        return call('disconnect');
    }
    function hasUncommittedChanges() {
        return call('hasUncommittedChanges');
    }
    function sync(paths) {
        return call('sync', paths);
    }
    function unsync(paths) {
        return call('unsync', paths);
    }
    async function call(method, ...args) {
        await tabExists;
        return tab.call(method, ...args);
    }
    function send(message) {
        tab.send(message);
    }
    function getPatch(ops, textType = defaultTextType) {
        return new JSONPatch(ops, textType);
    }
    function now() {
        return Date.now() + (state.get().serverTimeOffset || 0);
    }
    function handleMessage(event) {
        const { data } = event;
        if (!is(ChangeMessageSchema, data)) {
            onMessage(data); // Allow custom messages to be handled by the client
            return;
        }
        const { path, changes, doc, rev, deleted } = data;
        const clientDoc = clientDocs.get(path)?.deref();
        if (!clientDoc || !clientDoc.isLoaded.get())
            return;
        if (doc && rev) {
            // Entire doc needed updating
            clientDoc.receiveDoc(doc, rev, changes);
        }
        else if (changes) {
            clientDoc.receive(changes);
        }
        else if (deleted) {
            clientDocs.forEach((ref, docPath) => {
                if (typeof docPath === 'string' && (docPath === path || docPath.startsWith(path + '/'))) {
                    ref.deref()?.unload();
                    clientDocs.delete(docPath);
                }
            });
        }
    }
    function handleState(event) {
        state.set(event.data);
    }
    function createCollection(path, docConfig) {
        return (id) => createDoc(getPath(path, id), docConfig);
    }
    function createSubdocCollection(path, docConfig) {
        const subDocCollection = (id) => {
            return createDoc(getPath(path, id), docConfig);
        };
        // Unimpelemented on leader
        // subDocCollection.get = <T extends AsJson = JSONObject>(options?: SubdocGetOptions): Promise<T[]> => {
        //   return call('GET', path, null, options);
        // };
        return subDocCollection;
    }
    function createDoc(path, docConfig) {
        let doc = clientDocs.get(path)?.deref();
        if (doc)
            return doc;
        if (docConfig.type) {
            if (typeof path === 'string') {
                doc = getClientDoc(call, path, docConfig, docSyncs);
            }
            else {
                doc = proxyDoc(call, path, docConfig, docSyncs, clientDocs);
            }
        }
        else {
            doc = {};
        }
        clientDocs.set(path, new WeakRef(doc));
        if (docConfig.children) {
            for (const [name, childConfig] of Object.entries(docConfig.children)) {
                const subpath = getPath(path, name);
                if (childConfig.isCollection) {
                    doc[name] = createSubdocCollection(subpath, childConfig);
                }
                else {
                    doc[name] = createDoc(subpath, childConfig);
                }
            }
        }
        if (docConfig.access === 'role') {
            if (typeof path === 'string') {
                doc.roles = createDoc(`${path}/roles`, LWW_DOC);
                doc.roles.respondToInvite = async (inviteId, accept) => {
                    return call('respondToInvite', `${path}/roles`, inviteId, accept);
                };
            }
            else {
                doc.roles = proxyDoc(call, getPath(path, 'roles'), LWW_DOC, docSyncs, clientDocs);
                doc.roles.respondToInvite = async (inviteId, accept) => {
                    return call('respondToInvite', `${await whenReadable(path)}/roles`, inviteId, accept);
                };
            }
        }
        if (docConfig.versioned) {
            if (typeof path === 'string') {
                doc.versions = createDoc(`${path}/versions`, LWW_DOC);
                doc.versions.getVersion = async (rev) => {
                    return call('get', `${path}/versions/${rev}`);
                };
                doc.versions.getChanges = async (rev) => {
                    return call('get', `${path}/versions/${rev}/changes`);
                };
            }
            else {
                doc.versions = proxyDoc(call, getPath(path, 'versions'), LWW_DOC, docSyncs, clientDocs);
                doc.versions.getVersion = async (rev) => {
                    return call('get', `${await whenReadable(path)}/versions/${rev}`);
                };
                doc.versions.getChanges = async (rev) => {
                    return call('get', `${await whenReadable(path)}/versions/${rev}/changes`);
                };
            }
        }
        return doc;
    }
    /**
     * Creates a unique ID that will be safe for use in operational transformations as it will not be one that could be
     * confused with an array index and can be used to create maps by id. E.g. 0123 or ab9E but never 1234.
     * - The default length is 22 characters, a slightly larger number space than a 128 bit UUID.
     * - Optionally provide a map of IDs to avoid, e.g. { '0123': true }.
     * - Optionally add a prefix to the ID.
     */
    function createId(length = 22, avoid = EMPTY, prefix = '') {
        let id;
        while (avoid[(id = prefix + cryptoId(length))] || /^(0|[1-9]\d*)$/.test(id))
            ;
        return id;
    }
    /**
     * Create an API for docs to add their path to be synced. Will batch them up and call sync(paths: string[]) in one
     * go rather than calling sync for each doc.
     */
    function createDocSyncs() {
        let syncs;
        let syncsPromise;
        let unsyncs;
        let unsyncsPromise;
        return {
            sync(path) {
                if (!syncs) {
                    syncs = new Set();
                    syncsPromise = Promise.resolve().then(() => {
                        const paths = syncs;
                        syncs = undefined;
                        syncsPromise = undefined;
                        sync(paths);
                    });
                }
                syncs.add(path);
                return syncsPromise;
            },
            unsync(path) {
                if (!unsyncs) {
                    unsyncs = new Set();
                    unsyncsPromise = Promise.resolve().then(() => {
                        const paths = unsyncs;
                        unsyncs = undefined;
                        unsyncsPromise = undefined;
                        unsync(paths);
                    });
                }
                unsyncs.add(path);
                return unsyncsPromise;
            },
        };
    }
}
function getPath(part1, part2) {
    const part1IsString = typeof part1 === 'string';
    const part2IsString = typeof part2 === 'string';
    if (part1IsString && part2IsString)
        return `${part1}/${part2}`;
    return derived(() => {
        // If either part is null, return null
        if ((!part1IsString && part1.get() == null) || (!part2IsString && part2.get() == null)) {
            return null;
        }
        return `${part1IsString ? part1 : part1.get()}/${part2IsString ? part2 : part2.get()}`;
    });
}
